import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Crown, Medal, Info, X } from "lucide-react";
import { copyToClipboard } from "./GlobalFunctions";
import { slurpApi } from "../lib/slurpApi";

function HowItWorksModal({ onClose }) {
  // Click-out + Esc close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="pm-modal__bg" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pm-modal__close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h3 className="pm-modal__title">How the leaderboard works</h3>
        <p className="pm-modal__lede">
          <b>This board is built to reward holders, not flippers.</b>
          {" "}Speed-trading and ape-and-dump strategies barely register.
          The longer you sit on a position, the higher you climb.
        </p>
        <p>
          The whole point: incentivise <b>diamond-handing</b> Slurp
          tokens. Every other launchpad rewards loud volume and fast
          dumps. Slurp does the opposite. Quiet, patient holders win,
          and the leaderboard makes that visible to the whole community.
        </p>

        <h4 className="pm-modal__h4">Rewards</h4>
        <p>
          <b>12.5% of LP fees on every slurp token</b> goes into a
          weekly Diamond Hands prize pool. At the end of each 7-day
          cycle, slurp pays out the pool to the top wallets on the
          board. Real SOL, sent to your connected wallet, no claim
          flow needed.
        </p>
        <p>
          The bigger the week, the bigger the pool. Top of the board on
          a hot week could see meaningful payouts; just being in the top
          10 most weeks is enough to compound nicely.
        </p>

        <h4 className="pm-modal__h4">The formula</h4>
        <p>
          For each wallet across every Slurp token they've traded:
        </p>
        <pre className="pm-modal__code">
{`score = totalVolume^0.3 × avgHoldHours^0.7

avgHoldHours = Σ(usd × hours) / Σ(usd)`}
        </pre>
        <p>
          The <b>0.7 weight on hold time</b> vs only 0.3 on volume is
          the lever. A $1k position held 24h crushes a $100k position
          flipped in 1 minute. Bigger size helps; bigger size <i>without
          patience</i> doesn't.
        </p>

        <h4 className="pm-modal__h4">Anti-farm gates</h4>
        <p>
          Designed so flipping bots can't game it:
        </p>
        <ul className="pm-modal__list">
          <li>Positions held under <b>60 seconds</b> count as zero. No sniper-speed farming.</li>
          <li>Positions under <b>$20</b> count as zero. No penny-sized sybil farming.</li>
          <li>Protocol wallets (treasury, burn wallet) are always excluded.</li>
        </ul>

        <h4 className="pm-modal__h4">Examples</h4>
        <p>How the rules play out in practice:</p>
        <table className="pm-modal__table">
          <thead>
            <tr>
              <th>What you do</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Buy $1k, hold 7 days, never sell</td>
              <td><span className="pm-modal__outcome--good">Top of board</span></td>
            </tr>
            <tr>
              <td>Buy $1k, hold 6 days, sell everything</td>
              <td><span className="pm-modal__outcome--good">Still top</span></td>
            </tr>
            <tr>
              <td>$20+ each on multiple active Slurp tokens, hold the week</td>
              <td><span className="pm-modal__outcome--good">Decent score</span></td>
            </tr>
            <tr>
              <td>Buy $1k, sell $999 fast, hold $1 dust forever</td>
              <td><span className="pm-modal__outcome--meh">Almost zero</span></td>
            </tr>
            <tr>
              <td>Sniper: buy $10k, sell in 30 seconds</td>
              <td><span className="pm-modal__outcome--bad">Zero (60s gate)</span></td>
            </tr>
            <tr>
              <td>Buy $5, hold a year</td>
              <td><span className="pm-modal__outcome--bad">Zero ($20 gate)</span></td>
            </tr>
          </tbody>
        </table>

        <h4 className="pm-modal__h4">FIFO matching + partial sells</h4>
        <p>
          Sells close out your oldest open buy first. Realistic position
          tracking: what you sell first comes from what you bought first.
          If you buy and just sit, your hold time keeps growing every
          minute until you sell.
        </p>
        <p>
          Partial sells scale your score down proportionally. Sell 50%
          and only the remaining 50% keeps earning hold-time credit.
          Leave dust behind and you're scored on the dust, not the
          original buy. No gaming by selling out and "leaving 1 token".
        </p>

        <h4 className="pm-modal__h4">7-day rolling window</h4>
        <p>
          Only positions opened in the last 7 days count. After 7 days
          they roll off the board, so the leaderboard is always a
          rolling sprint of the current week's diamond hands. Old wins
          don't carry forever. Keep playing.
        </p>

        <h4 className="pm-modal__h4">When does it update</h4>
        <p>
          Recomputes hourly from on-chain trade data. No self-reporting,
          no honor system. Every position is auditable on Solscan.
        </p>
      </div>
    </div>
  );
}

const fmtScore = (n) => {
  if (n == null || Number.isNaN(+n)) return "0";
  const v = +n;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  if (v >= 100) return v.toFixed(0);
  return v.toFixed(2);
};
const shortAddr = (a) => (!a ? "—" : `${a.slice(0, 4)}…${a.slice(-4)}`);

const adaptRow = (row) => ({
  rank: row.rank,
  address: row.wallet,
  score: Number(row.score) || 0,
  username: row.username || null,
  imageUrl: row.image_url || null,
});

function PodiumCard({ trader }) {
  const navigate = useNavigate();
  const { rank, address, score, username, imageUrl } = trader;
  const tone = rank === 1 ? "gold" : rank === 2 ? "silver" : "bronze";
  const Icon = rank === 1 ? Crown : Medal;
  const label = rank === 1 ? "Diamond hand" : rank === 2 ? "Runner-up" : "Third place";
  const display = username ? `@${username}` : shortAddr(address);
  return (
    <button
      type="button"
      className={`pm-lb2__podium-card pm-lb2__podium-card--${tone}`}
      onClick={() => navigate(`/profile/${address}`)}
      title="View profile"
    >
      {rank === 1 && (
        <img
          src="/images/webp/Gold-Crown-28.png"
          alt="Champion"
          className="pm-lb2__crown"
          aria-hidden="true"
        />
      )}
      <div className="pm-lb2__podium-top">
        <span className={`pm-lb2__podium-medal pm-lb2__podium-medal--${tone}`}>
          <Icon size={14} /> {label}
        </span>
        <span className="pm-lb2__podium-rank">#{rank}</span>
      </div>
      {imageUrl && (
        <img className="pm-lb2__podium-avatar" src={imageUrl} alt="" />
      )}
      <div className="pm-lb2__podium-addr">{display}</div>
      <div className="pm-lb2__podium-vol">{fmtScore(score)}</div>
    </button>
  );
}

function MiniCard({ trader, leaderScore }) {
  const navigate = useNavigate();
  const { rank, address, score, username, imageUrl } = trader;
  const pct = Math.max(4, Math.round((score / leaderScore) * 100));
  const display = username ? `@${username}` : shortAddr(address);
  return (
    <button
      type="button"
      className="pm-lb2__mini"
      onClick={() => navigate(`/profile/${address}`)}
      title="View profile"
    >
      <span className="pm-lb2__mini-rank">#{rank}</span>
      {imageUrl && (
        <img className="pm-lb2__mini-avatar" src={imageUrl} alt="" />
      )}
      <div className="pm-lb2__mini-body">
        <div className="pm-lb2__mini-row">
          <span className="pm-lb2__mini-addr">{display}</span>
          <span className="pm-lb2__mini-vol">{fmtScore(score)}</span>
        </div>
        <div className="pm-lb2__mini-bar" aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>
    </button>
  );
}

export default function Leaderboard() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    slurpApi
      .getLeaderboardScore(100)
      .then((res) => {
        if (cancelled) return;
        setRawRows(Array.isArray(res?.leaderboard) ? res.leaderboard : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err?.message || "Couldn't load leaderboard");
        setRawRows([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const rankedRows = useMemo(
    () => rawRows.map((r, i) => ({ ...r, rank: i + 1 })),
    [rawRows]
  );
  const traders = useMemo(() => rankedRows.map(adaptRow), [rankedRows]);

  const top3 = traders.slice(0, 3);
  const rest = traders.slice(3);
  const leaderScore = top3[0]?.score || 1;
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div className="page-content pm-lb2">
      <div className="pm-lb__hero">
        <video
          className="pm-lb__video"
          src="/images/webp/slurpleaderboard.MP4"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="pm-lb__copy">
          <div className="pm-lb__eyebrow">
            <img
              src="/images/png/slurpface (1).png"
              alt=""
              className="pm-lb__eyebrow-face"
            />
            SLURP · WEEKLY LEADERBOARD
          </div>
          <h1 className="pm-lb__title">
            Leaderboard.<br />
            <span className="pm-hero-line2">
              <span className="pm-lb__hw">Hold &amp; win.</span>
              <button
                type="button"
                className="pm-hero-sticker pm-hero-sticker--desktop"
                onClick={() => setHowOpen(true)}
              >
                <Info size={14} /> See how it works
              </button>
            </span>
          </h1>
        </div>
        <div className="pm-lb__sub">
          <p>
            Rewards real holders, not wash traders or serial launchers. Sit on
            your slurp tokens and your score climbs. Top wallets each week
            split the prize pool.
          </p>
          <button
            type="button"
            className="pm-hero-sticker pm-hero-sticker--mobile"
            onClick={() => setHowOpen(true)}
          >
            <Info size={14} /> See how it works
          </button>
        </div>
      </div>

      {loading && traders.length === 0 ? (
        <div className="pm-slurp-loader" role="status" aria-live="polite">
          <img
            src="/images/png/slurpface (1).png"
            alt="Slurp"
            style={{ width: 72, height: 72 }}
          />
          <span className="pm-slurp-loader__label">Loading traders</span>
        </div>
      ) : fetchError ? (
        <div className="pm-empty" style={{ padding: 28, textAlign: "center", color: "var(--pm-down)" }}>
          {fetchError}
        </div>
      ) : traders.length === 0 ? (
        <div className="pm-empty" style={{ padding: 28, textAlign: "center" }}>
          No qualifying trades in the last 7 days yet.
        </div>
      ) : (
        <>
          <div className="pm-lb2__podium">
            {podiumOrder.map((t) => (
              <PodiumCard key={t.address} trader={t} />
            ))}
          </div>

          <div className="pm-lb2__grid">
            {rest.map((t) => (
              <MiniCard key={t.address} trader={t} leaderScore={leaderScore} />
            ))}
          </div>
        </>
      )}

      {howOpen && <HowItWorksModal onClose={() => setHowOpen(false)} />}
    </div>
  );
}
